import { canDirectContact } from '@/utils/dealerContactGate';
import { trackContactClick } from '@/utils/contactTracking';
/**
 * CarCardList - Autovit-style horizontal listing card
 * Image left (~35%), content right with prominent price
 */
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/lib/router-compat";
import { ga4SelectItem } from "@/utils/analytics";
import { useViewTracking } from "@/hooks/useAnalytics";
import { useEffect, memo, useMemo } from "react";
import { FavoriteButton } from "@/components/listings/card/FavoriteButton";
import { useCompare } from "@/contexts/CompareContext";
import { ArrowsLeftRight as ArrowLeftRight, Phone, MapPin, Gauge, Calendar, GasPump as Fuel, Camera, Check } from "@phosphor-icons/react";
import { ImagePlaceholder, hasValidPhoto } from "@/components/ui/ImagePlaceholder";
import { OptimizedImage } from "@/components/ui/optimized-image";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useCountry } from "@/hooks/useCountry";
import { localizeTransmission, localizeFuel } from "@/utils/listingLocalization";
import { useCurrencyConversion } from "@/hooks/useCurrencyConversion";
import { selectBestCardImage } from "@/utils/imageSelection";
import { generateThumbnailAlt } from "@/utils/imageAltGenerator";
import { cn } from "@/lib/utils";
import { WhatsAppCardButton } from "@/components/listings/detail/WhatsAppCardButton";

type Props = {
  id?: string;
  title: string;
  price: number;
  currency?: string;
  year: number;
  mileageKm: number;
  location: string;
  location_city?: string;
  location_region?: string;
  location_country?: string;
  photo?: string;
  slug: string;
  make?: string;
  model?: string;
  promoted?: boolean;
  auto_promoted?: boolean;
  is_new_listing?: boolean;
  status?: 'active' | 'sold' | 'inactive' | 'hidden' | 'reserved' | 'expired';
  vehicle_type?: string;
  verified_dealer?: boolean;
  vin_available?: boolean;
  certified_vehicle?: boolean;
  fuel_type?: string;
  transmission?: string;
  mileage?: number;
  engine_power?: number;
  images?: string[];
  created_at?: string;
  country_code?: string;
  user_id?: string;
};

const CarCardListComponent = ({ 
  id,
  title, 
  price,
  currency,
  year, 
  mileageKm, 
  location,
  location_city,
  location_region,
  location_country,
  photo, 
  slug, 
  make, 
  model, 
  promoted = false,
  auto_promoted = false,
  is_new_listing = false,
  status = 'active',
  vehicle_type = 'car',
  verified_dealer = false,
  vin_available = false,
  certified_vehicle = false,
  fuel_type = '',
  transmission = '',
  mileage = 0,
  engine_power,
  images = [],
  created_at,
  country_code,
  user_id,
}: Props) => {
  const { trackView } = useViewTracking();
  const { addToCompare, removeFromCompare, isInCompare } = useCompare();
  const { country } = useCountry();
  const { formatPrice } = useCurrencyConversion();

  const inCompare = id ? isInCompare(id) : false;

  // Smart image selection
  const bestPhoto = selectBestCardImage(images, photo);
  const hasPhoto = hasValidPhoto(bestPhoto);
  const imageCount = images?.length || (photo ? 1 : 0);

  // SEO alt text
  const imageAlt = useMemo(() => generateThumbnailAlt({
    make: make || '',
    model: model || '',
    year,
    price,
    currency: currency || 'EUR',
    location_city
  }), [make, model, year, price, currency, location_city]);

  // Calculate "updated ago" text
  const getUpdatedAgo = () => {
    if (!created_at) return null;
    const now = new Date();
    const created = new Date(created_at);
    const hoursDiff = Math.floor((now.getTime() - created.getTime()) / (1000 * 60 * 60));
    
    if (hoursDiff < 1) return "Acum";
    if (hoursDiff < 24) return `Acum ${hoursDiff} ore`;
    const daysDiff = Math.floor(hoursDiff / 24);
    if (daysDiff === 1) return "Ieri";
    if (daysDiff < 7) return `Acum ${daysDiff} zile`;
    return null;
  };

  const updatedAgo = getUpdatedAgo();

  // Location display - Autovit style
  const locationDisplay = [
    location_city, 
    location_region ? `(${location_region})` : null
  ].filter(Boolean).join(' ') || location;

  useEffect(() => {
    if (id) trackView(id);
  }, [id, trackView]);

  const handleClick = () => {
    ga4SelectItem({
      id: id || slug,
      title,
      make,
      model,
      year,
      price
    });
    try {
      sessionStorage.setItem('returnToPath', window.location.pathname + window.location.search);
      sessionStorage.setItem('returnToId', id || slug);
      const key = `scroll:${window.location.pathname}`;
      sessionStorage.setItem(key, String(window.scrollY));
    } catch {}
  };

  const handleCompareToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!id) return;
    
    if (inCompare) {
      removeFromCompare(id);
    } else {
      addToCompare({
        id,
        slug,
        make: make || '',
        model: model || '',
        year,
        price,
        mileage: mileage || mileageKm,
        fuel_type: fuel_type || '',
        transmission: transmission || '',
        engine_power,
        images: images.length > 0 ? images : photo ? [photo] : [],
        vehicle_type: vehicle_type || 'car',
      });
    }
  };

  const handleContact = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!id) return;
    
    if (!(await canDirectContact(user_id))) { toast.info("Trimite o cerere din pagina anunțului, dealerul te va contacta."); return; }
    try {
      const { data: contactInfo } = await supabase
        .rpc('get_public_contact_info', { _listing_id: id });
      
      if (contactInfo && contactInfo.length > 0) {
        const phone = contactInfo[0].contact_phone;
        if (phone) {
          trackContactClick({ id, sellerId: user_id }, 'phone', 'list_card');
          window.location.href = `tel:${phone}`;
        } else {
          toast.error("Număr de telefon indisponibil");
        }
      }
    } catch (error) {
      console.error('Error fetching contact:', error);
      toast.error("Eroare la contactare");
    }
  };

  return (
    <Link 
      to={id ? `/cars/details/${id}` : `/cars/${slug}`} 
      aria-label={title} 
      className="block" 
      onClick={handleClick} 
      data-listing-id={(id || slug)}
    >
      <Card className={cn(
        "overflow-hidden transition-all duration-200 hover:shadow-lg bg-card relative",
        promoted && "ring-2 ring-primary shadow-md",
        status === 'sold' && "opacity-75"
      )}>
        <div className="flex">
          {/* Image Section - Left ~35% */}
          <div className="relative w-[140px] sm:w-[200px] md:w-[240px] flex-shrink-0">
            <div className="relative h-full min-h-[120px] sm:min-h-[150px]">
              {hasPhoto ? (
                <OptimizedImage
                  src={bestPhoto!}
                  alt={imageAlt}
                  className={cn(
                    "w-full h-full object-cover",
                    status === 'sold' && "grayscale"
                  )}
                  width={240}
                  height={180}
                  sizes="(max-width: 640px) 140px, (max-width: 768px) 200px, 240px"
                />
              ) : (
                <ImagePlaceholder iconSize="md" />
              )}

              {/* Photo count - Autovit style */}
              {imageCount > 1 && (
                <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-1 rounded-sm text-xs font-medium flex items-center gap-1">
                  <Camera className="w-3 h-3" />
                  {imageCount}
                </div>
              )}

              {/* Favorite button */}
              {id && (
                <div className="absolute top-2 right-2 z-20" onClick={(e) => e.preventDefault()}>
                  <FavoriteButton 
                    listingId={id} 
                    title={title}
                    size="sm"
                    variant="ghost"
                  />
                </div>
              )}

              {/* Sold overlay */}
              {status === 'sold' && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <Badge variant="destructive" className="font-bold">VÂNDUT</Badge>
                </div>
              )}
            </div>
          </div>

          {/* Content Section - Right ~65% */}
          <div className="flex-1 p-3 sm:p-4 flex flex-col justify-between min-w-0">
            {/* Top Row: Title + Price */}
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-foreground text-sm sm:text-base line-clamp-1 mb-0.5">
                  {title}
                </h3>
                {engine_power && (
                  <p className="text-xs text-muted-foreground">
                    {engine_power} CP
                  </p>
                )}
              </div>
              
              {/* Price */}
              <div className="text-right flex-shrink-0">
                <div className="text-xl sm:text-2xl font-extrabold text-foreground whitespace-nowrap">
                  {formatPrice(price, currency || 'EUR')}
                </div>
                <span className="text-xs text-primary hover:underline cursor-pointer">
                  Calculează rata
                </span>
              </div>
            </div>

            {/* Badges Row */}
            <div className="flex flex-wrap gap-1.5 mb-2">
              {promoted && (
                <Badge variant="secondary" className="text-[10px] px-2 py-0.5 bg-accent text-accent-foreground font-medium">
                  Promovat
                </Badge>
              )}
              {verified_dealer && (
                <Badge variant="secondary" className="text-[10px] px-2 py-0.5 bg-secondary text-secondary-foreground font-medium flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  Detalii verificate
                </Badge>
              )}
            </div>

            {/* Specs Row */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs sm:text-sm text-muted-foreground mb-2">
              <span className="flex items-center gap-1.5">
                <Gauge className="w-4 h-4 text-muted-foreground/70" />
                {new Intl.NumberFormat('ro-RO').format(mileageKm)} km
              </span>
              {fuel_type && (
                <span className="flex items-center gap-1.5">
                  <Fuel className="w-4 h-4 text-muted-foreground/70" />
                  {localizeFuel(fuel_type, 'ro')}
                </span>
              )}
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4 text-muted-foreground/70" />
                {year}
              </span>
            </div>

            {/* Location Row */}
            <div className="text-xs text-muted-foreground">
              <div className="flex items-center gap-1 mb-0.5">
                <MapPin className="w-3 h-3 flex-shrink-0" />
                <span className="line-clamp-1">{locationDisplay}</span>
              </div>
              <div className="flex items-center gap-1 text-[11px]">
                <span>Privat</span>
                {updatedAgo && (
                  <>
                    <span>•</span>
                    <span>Reactualizat {updatedAgo.toLowerCase()}</span>
                  </>
                )}
              </div>
            </div>

            {/* Action Buttons - Desktop only */}
            <div className="hidden sm:flex gap-2 mt-3">
              {id && (
                <>
                  <Button
                    onClick={handleContact}
                    size="sm"
                    className="flex-1"
                    variant="default"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Sună
                  </Button>
                  <WhatsAppCardButton
                    listingId={id}
                    listingTitle={title}
                    sellerId={user_id}
                    size="sm"
                    className="flex-1"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="px-3"
                    onClick={handleCompareToggle}
                    title={inCompare ? "Elimină din comparare" : "Adaugă la comparare"}
                  >
                    <ArrowLeftRight className="w-4 h-4" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </Card>
    </Link>
  );
};

// Memoize component to prevent unnecessary re-renders
export const CarCardList = memo(CarCardListComponent);
